class KiwibitVideoShowcaseCarousel extends HTMLElement {
  connectedCallback() {
    this.desktopAlignedWidth = 1318;
    this.desktopRightBleed = 170;
    this.desktopSafeGutter = 64;
    this.desktopViewportWidth = this.desktopAlignedWidth + this.desktopRightBleed;
    this.desktopContainer = this.querySelector('.kiwibit-video-showcase__desktop');
    this.desktopTrack = this.querySelector('[data-kiwibit-video-desktop-track]');
    this.desktopGroups = this.desktopTrack ? Array.from(this.desktopTrack.querySelectorAll('[data-kiwibit-video-group]')) : [];
    this.desktopCards = this.desktopTrack ? Array.from(this.desktopTrack.querySelectorAll('[data-kiwibit-video-card]')) : [];
    this.mobileScroller = this.querySelector('[data-kiwibit-video-mobile-scroller]');
    this.mobileGroups = this.mobileScroller ? Array.from(this.mobileScroller.querySelectorAll('[data-kiwibit-video-mobile-group]')) : [];
    this.mobileCards = this.mobileScroller ? Array.from(this.mobileScroller.querySelectorAll('[data-kiwibit-video-card]')) : [];
    this.prevButton = this.querySelector('[data-kiwibit-video-prev]');
    this.nextButton = this.querySelector('[data-kiwibit-video-next]');
    this.cards = Array.from(this.querySelectorAll('[data-kiwibit-video-card]'));
    this.playButtons = Array.from(this.querySelectorAll('[data-kiwibit-video-play]'));
    this.videos = Array.from(this.querySelectorAll('video.kiwibit-video-showcase__video'));
    this.desktopIndex = 0;
    this.desktopSnapPoints = [];
    this.desktopFrame = null;
    this.mobileFrame = null;
    this.mobileScrollFrame = null;
    this.mobileDragFrame = null;
    this.mobileDragScroll = 0;
    this.mobileSnapPoints = [];
    this.mobileAnimating = false;
    this.mobileIndex = 0;
    this.mobileTouching = false;
    this.mobileStartX = 0;
    this.mobileStartY = 0;
    this.mobileDeltaX = 0;
    this.mobileDeltaY = 0;
    this.mobileStartScroll = 0;
    this.mobileStartPage = 0;

    this.onResize = this.handleResize.bind(this);
    this.onMobileScroll = this.scheduleMobileState.bind(this);
    this.onMobileTouchStart = this.handleMobileTouchStart.bind(this);
    this.onMobileTouchMove = this.handleMobileTouchMove.bind(this);
    this.onMobileTouchEnd = this.handleMobileTouchEnd.bind(this);
    this.onPrevClick = this.scrollDesktop.bind(this, -1);
    this.onNextClick = this.scrollDesktop.bind(this, 1);
    this.onPlayClick = this.handlePlayClick.bind(this);
    this.onVideoPause = this.handleVideoPause.bind(this);
    this.onVideoEnded = this.handleVideoEnded.bind(this);

    if (this.prevButton) this.prevButton.addEventListener('click', this.onPrevClick);
    if (this.nextButton) this.nextButton.addEventListener('click', this.onNextClick);
    if (this.mobileScroller) this.mobileScroller.addEventListener('scroll', this.onMobileScroll, { passive: true });
    if (this.mobileScroller) this.mobileScroller.addEventListener('touchstart', this.onMobileTouchStart, { passive: true });
    if (this.mobileScroller) this.mobileScroller.addEventListener('touchmove', this.onMobileTouchMove, { passive: false });
    if (this.mobileScroller) this.mobileScroller.addEventListener('touchend', this.onMobileTouchEnd);
    if (this.mobileScroller) this.mobileScroller.addEventListener('touchcancel', this.onMobileTouchEnd);
    this.playButtons.forEach((button) => button.addEventListener('click', this.onPlayClick));
    this.videos.forEach((video) => {
      video.addEventListener('pause', this.onVideoPause);
      video.addEventListener('ended', this.onVideoEnded);
    });

    window.addEventListener('resize', this.onResize);
    this.handleResize();
  }

  disconnectedCallback() {
    if (this.desktopFrame) cancelAnimationFrame(this.desktopFrame);
    if (this.mobileFrame) cancelAnimationFrame(this.mobileFrame);
    if (this.mobileScrollFrame) cancelAnimationFrame(this.mobileScrollFrame);
    if (this.mobileDragFrame) cancelAnimationFrame(this.mobileDragFrame);
    window.removeEventListener('resize', this.onResize);
    if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrevClick);
    if (this.nextButton) this.nextButton.removeEventListener('click', this.onNextClick);
    if (this.mobileScroller) this.mobileScroller.removeEventListener('scroll', this.onMobileScroll);
    if (this.mobileScroller) this.mobileScroller.removeEventListener('touchstart', this.onMobileTouchStart);
    if (this.mobileScroller) this.mobileScroller.removeEventListener('touchmove', this.onMobileTouchMove);
    if (this.mobileScroller) this.mobileScroller.removeEventListener('touchend', this.onMobileTouchEnd);
    if (this.mobileScroller) this.mobileScroller.removeEventListener('touchcancel', this.onMobileTouchEnd);
    this.playButtons.forEach((button) => button.removeEventListener('click', this.onPlayClick));
    this.videos.forEach((video) => {
      video.removeEventListener('pause', this.onVideoPause);
      video.removeEventListener('ended', this.onVideoEnded);
    });
  }

  handleResize() {
    const mobileMainWidth = Math.min(Math.max(window.innerWidth - 74, 286), 316);
    const mobileSideWidth = mobileMainWidth;
    const mobileGap = 16;
    const mobileMainHeight = Math.round(mobileMainWidth * 675 / 429);
    const mobileGroupWidth = mobileMainWidth + mobileSideWidth + mobileMainWidth + mobileMainWidth + (mobileGap * 3);

    this.style.setProperty('--kiwibit-video-desktop-group-width', `${this.desktopAlignedWidth}px`);
    this.style.setProperty('--kiwibit-video-mobile-main-width', `${mobileMainWidth}px`);
    this.style.setProperty('--kiwibit-video-mobile-main-height', `${mobileMainHeight}px`);
    this.style.setProperty('--kiwibit-video-mobile-side-width', `${mobileSideWidth}px`);
    this.style.setProperty('--kiwibit-video-mobile-gap', `${mobileGap}px`);
    this.style.setProperty('--kiwibit-video-mobile-group-width', `${mobileGroupWidth}px`);

    this.mobileSnapPoints = [];
    this.updateMobileSnapMode();
    this.measureDesktop();
    this.updateDesktop(false);
    this.scheduleMobileState();
  }

  updateMobileSnapMode() {
    if (!this.mobileScroller) return;

    this.mobileScroller.style.scrollSnapType = this.isMobileLayout() ? 'none' : '';
  }

  measureDesktop() {
    if (!this.desktopGroups.length) {
      this.desktopSnapPoints = [0];
      this.desktopIndex = 0;
      return;
    }

    const lastGroup = this.desktopGroups[this.desktopGroups.length - 1];
    const trackWidth = lastGroup.offsetLeft + lastGroup.offsetWidth;
    const maxOffset = Math.max(trackWidth - this.desktopViewportWidth, 0);
    const snapPoints = [0];

    const pushUniquePoint = (value) => {
      const lastPoint = snapPoints[snapPoints.length - 1];
      if (Math.abs(lastPoint - value) > 1) {
        snapPoints.push(value);
      }
    };

    this.desktopGroups.forEach((group, index) => {
      if (index === 0) return;
      const targetOffset = Math.max(0, Math.min(group.offsetLeft - this.desktopSafeGutter, maxOffset));
      pushUniquePoint(targetOffset);
    });

    if (maxOffset > 0) {
      pushUniquePoint(maxOffset);
    }

    this.desktopSnapPoints = snapPoints;

    if (this.desktopIndex >= this.desktopSnapPoints.length) {
      this.desktopIndex = Math.max(this.desktopSnapPoints.length - 1, 0);
    }
  }

  scrollDesktop(direction) {
    if (!this.desktopGroups.length) return;

    const nextIndex = Math.max(0, Math.min(this.desktopSnapPoints.length - 1, this.desktopIndex + direction));
    if (nextIndex === this.desktopIndex) return;

    this.desktopIndex = nextIndex;
    this.updateDesktop(true);
    this.pauseInactiveGroupVideos();
  }

  updateDesktop(animate) {
    if (!this.desktopTrack || !this.desktopGroups.length) return;

    const offset = this.desktopSnapPoints[this.desktopIndex] || 0;
    this.desktopTrack.style.transition = animate
      ? 'transform 460ms cubic-bezier(0.22, 1, 0.36, 1)'
      : 'none';
    this.desktopTrack.style.transform = `translate3d(${-offset}px, 0, 0)`;

    if (this.desktopContainer) {
      this.desktopContainer.classList.toggle('is-cards-state', this.desktopIndex > 0);
      this.desktopContainer.classList.toggle('has-left-fade', this.desktopIndex > 0);
      this.desktopContainer.classList.toggle('has-right-fade', this.desktopIndex < this.desktopSnapPoints.length - 1);
    }

    this.updateDesktopEdgeCards();

    if (this.prevButton) {
      const atStart = this.desktopIndex <= 0;
      this.prevButton.disabled = atStart;
      this.prevButton.classList.toggle('is-hidden', atStart);
    }

    if (this.nextButton) {
      const atEnd = this.desktopIndex >= this.desktopSnapPoints.length - 1;
      this.nextButton.disabled = atEnd;
      this.nextButton.classList.toggle('is-hidden', atEnd);
    }
  }

  updateDesktopEdgeCards() {
    if (!this.desktopCards.length) return;

    const viewportLeft = this.desktopSnapPoints[this.desktopIndex] || 0;
    const viewportRight = viewportLeft + this.desktopViewportWidth;
    const edgeThreshold = 1;

    this.desktopCards.forEach((card) => {
      card.classList.remove('is-edge-partial-left', 'is-edge-partial-right');

      const cardLeft = card.offsetLeft;
      const cardRight = cardLeft + card.offsetWidth;
      const isLeftPartial = cardLeft < viewportLeft - edgeThreshold && cardRight > viewportLeft + edgeThreshold;
      const isRightPartial = cardLeft < viewportRight - edgeThreshold && cardRight > viewportRight + edgeThreshold;

      if (isLeftPartial) {
        card.classList.add('is-edge-partial-left');
      }

      if (isRightPartial) {
        card.classList.add('is-edge-partial-right');
      }
    });
  }

  scheduleMobileState() {
    if (this.mobileTouching || this.mobileAnimating) return;
    if (this.mobileFrame) return;

    this.mobileFrame = requestAnimationFrame(() => {
      this.mobileFrame = null;
      this.updateMobileEdgeCards();
    });
  }

  isMobileLayout() {
    return window.matchMedia('(max-width: 989.98px)').matches;
  }

  getMobileSnapPoints() {
    if (!this.mobileScroller) return [0];

    const scrollerRect = this.mobileScroller.getBoundingClientRect();
    const maxScrollLeft = Math.max(this.mobileScroller.scrollWidth - this.mobileScroller.clientWidth, 0);
    const points = [];
    const addPoint = (value) => {
      const point = Math.max(0, Math.min(value, maxScrollLeft));

      if (!points.some((existingPoint) => Math.abs(existingPoint - point) < 12)) {
        points.push(point);
      }
    };
    const getCardPoint = (card) => {
      const cardRect = card.getBoundingClientRect();
      return cardRect.left - scrollerRect.left + this.mobileScroller.scrollLeft;
    };

    addPoint(0);

    if (this.mobileGroups.length) {
      this.mobileGroups.forEach((group) => {
        const groupCards = [
          group.querySelector('.kiwibit-video-showcase__card--mobile-1'),
          group.querySelector('.kiwibit-video-showcase__card--mobile-2'),
          group.querySelector('.kiwibit-video-showcase__card--mobile-4'),
          group.querySelector('.kiwibit-video-showcase__card--mobile-5'),
        ].filter(Boolean);

        groupCards.forEach((card) => addPoint(getCardPoint(card)));
      });
    } else {
      this.mobileCards.forEach((card) => addPoint(getCardPoint(card)));
    }

    if (points.length === 1 && maxScrollLeft > 0) {
      addPoint(maxScrollLeft);
    }

    return points.sort((a, b) => a - b);
  }

  getMobileMaxPage() {
    return Math.max(this.getMobileSnapPoints().length - 1, 0);
  }

  getMobileNearestPage(points = this.getMobileSnapPoints()) {
    if (!this.mobileScroller) return 0;
    if (!points.length) return 0;

    const scrollLeft = this.mobileScroller.scrollLeft;

    return points.reduce((nearestIndex, point, index) => {
      const nearestDistance = Math.abs(points[nearestIndex] - scrollLeft);
      const distance = Math.abs(point - scrollLeft);
      return distance < nearestDistance ? index : nearestIndex;
    }, 0);
  }

  scrollToMobilePage(page, animate = true, points = this.getMobileSnapPoints()) {
    if (!this.mobileScroller) return;

    const nextPage = Math.max(0, Math.min(points.length - 1, page));
    const targetLeft = points[nextPage] || 0;

    this.mobileIndex = this.getMobileNearestGroupIndex(targetLeft);
    this.animateMobileScroll(targetLeft, animate);
  }

  animateMobileScroll(targetLeft, animate = true) {
    if (!this.mobileScroller) return;

    if (this.mobileScrollFrame) {
      cancelAnimationFrame(this.mobileScrollFrame);
      this.mobileScrollFrame = null;
    }

    if (!animate) {
      this.mobileAnimating = false;
      this.mobileScroller.scrollLeft = targetLeft;
      this.updateMobileEdgeCards();
      return;
    }

    const startLeft = this.mobileScroller.scrollLeft;
    const distance = targetLeft - startLeft;
    const duration = 360;
    const startTime = performance.now();
    this.mobileAnimating = true;

    const tick = (now) => {
      const progress = Math.min((now - startTime) / duration, 1);
      const easedProgress = 1 - Math.pow(1 - progress, 3);
      this.mobileScroller.scrollLeft = startLeft + distance * easedProgress;

      if (progress < 1) {
        this.mobileScrollFrame = requestAnimationFrame(tick);
        return;
      }

      this.mobileScroller.scrollLeft = targetLeft;
      this.mobileScrollFrame = null;
      this.mobileAnimating = false;
      this.updateMobileEdgeCards();
    };

    this.mobileScrollFrame = requestAnimationFrame(tick);
  }

  setMobileDragScroll(targetLeft) {
    if (!this.mobileScroller) return;

    this.mobileDragScroll = targetLeft;

    if (this.mobileDragFrame) return;

    this.mobileDragFrame = requestAnimationFrame(() => {
      this.mobileDragFrame = null;
      this.mobileScroller.scrollLeft = this.mobileDragScroll;
    });
  }

  getMobileNearestGroupIndex(targetLeft = 0) {
    if (!this.mobileGroups.length) return 0;

    return this.mobileGroups.reduce((nearestIndex, group, index) => {
      const nearestDistance = Math.abs(this.mobileGroups[nearestIndex].offsetLeft - targetLeft);
      const distance = Math.abs(group.offsetLeft - targetLeft);
      return distance < nearestDistance ? index : nearestIndex;
    }, 0);
  }

  handleMobileTouchStart(event) {
    if (!this.isMobileLayout() || !this.mobileScroller) return;

    if (this.mobileScrollFrame) {
      cancelAnimationFrame(this.mobileScrollFrame);
      this.mobileScrollFrame = null;
    }
    this.mobileAnimating = false;
    if (this.mobileDragFrame) {
      cancelAnimationFrame(this.mobileDragFrame);
      this.mobileDragFrame = null;
    }

    this.updateMobileSnapMode();
    this.mobileSnapPoints = this.getMobileSnapPoints();
    this.mobileTouching = true;
    this.mobileStartX = event.touches && event.touches.length ? event.touches[0].clientX : 0;
    this.mobileStartY = event.touches && event.touches.length ? event.touches[0].clientY : 0;
    this.mobileDeltaX = 0;
    this.mobileDeltaY = 0;
    this.mobileStartScroll = this.mobileScroller.scrollLeft;
    this.mobileStartPage = this.getMobileNearestPage(this.mobileSnapPoints);

  }

  handleMobileTouchMove(event) {
    if (!this.isMobileLayout() || !this.mobileScroller || !this.mobileTouching) return;

    const touch = event.touches && event.touches.length ? event.touches[0] : null;
    if (!touch) return;

    this.mobileDeltaX = touch.clientX - this.mobileStartX;
    this.mobileDeltaY = touch.clientY - this.mobileStartY;

    if (Math.abs(this.mobileDeltaX) > Math.abs(this.mobileDeltaY) && Math.abs(this.mobileDeltaX) > 8) {
      event.preventDefault();
      const points = this.mobileSnapPoints.length ? this.mobileSnapPoints : this.getMobileSnapPoints();
      const minScroll = points[Math.max(this.mobileStartPage - 1, 0)] || 0;
      const maxScroll = points[Math.min(this.mobileStartPage + 1, points.length - 1)] || minScroll;
      const nextScroll = this.mobileStartScroll - this.mobileDeltaX;
      this.setMobileDragScroll(Math.max(minScroll, Math.min(maxScroll, nextScroll)));
    }
  }

  handleMobileTouchEnd(event) {
    if (!this.isMobileLayout() || !this.mobileScroller || !this.mobileTouching) return;

    const endTouch = event.changedTouches && event.changedTouches.length ? event.changedTouches[0] : null;
    const endX = endTouch ? endTouch.clientX : this.mobileStartX;
    const endY = endTouch ? endTouch.clientY : this.mobileStartY;
    const dragDistance = this.mobileStartX - endX;
    const verticalDistance = this.mobileStartY - endY;
    const swipeThreshold = 48;
    let nextPage = this.mobileStartPage;

    if (Math.abs(dragDistance) >= swipeThreshold && Math.abs(dragDistance) > Math.abs(verticalDistance)) {
      nextPage += dragDistance > 0 ? 1 : -1;
    } else {
      nextPage = this.getMobileNearestPage(this.mobileSnapPoints);
    }

    this.mobileTouching = false;
    if (this.mobileDragFrame) {
      cancelAnimationFrame(this.mobileDragFrame);
      this.mobileDragFrame = null;
    }
    this.mobileDeltaX = 0;
    this.mobileDeltaY = 0;
    this.scrollToMobilePage(nextPage, true, this.mobileSnapPoints);
    this.pauseInactiveMobileGroupVideos();
  }

  updateMobileEdgeCards() {
    if (!this.mobileScroller || !this.mobileCards.length) return;

    const scrollerRect = this.mobileScroller.getBoundingClientRect();
    const edgeThreshold = 1;

    this.mobileCards.forEach((card) => {
      card.classList.remove('is-edge-partial-left', 'is-edge-partial-right');

      const cardRect = card.getBoundingClientRect();
      const isVisible = cardRect.right > scrollerRect.left + edgeThreshold && cardRect.left < scrollerRect.right - edgeThreshold;
      const isLeftPartial = isVisible && cardRect.left < scrollerRect.left - edgeThreshold && cardRect.right > scrollerRect.left + edgeThreshold;
      const isRightPartial = isVisible && cardRect.left < scrollerRect.right - edgeThreshold && cardRect.right > scrollerRect.right + edgeThreshold;

      if (isLeftPartial) {
        card.classList.add('is-edge-partial-left');
      }

      if (isRightPartial) {
        card.classList.add('is-edge-partial-right');
      }
    });
  }

  handlePlayClick(event) {
    const button = event.currentTarget;
    const card = button.closest('[data-kiwibit-video-card]');
    const video = card ? card.querySelector('video.kiwibit-video-showcase__video') : null;
    if (!card || !video) return;

    this.pauseAllVideos(video);
    card.classList.add('is-playing');
    video.controls = true;

    const playPromise = video.play();
    if (playPromise && typeof playPromise.catch === 'function') {
      playPromise.catch(() => {});
    }
  }

  handleVideoPause(event) {
    const video = event.currentTarget;
    if (!video.ended && !video.seeking) {
      const card = video.closest('[data-kiwibit-video-card]');
      if (card) card.classList.remove('is-playing');
      video.controls = false;
    }
  }

  handleVideoEnded(event) {
    const video = event.currentTarget;
    const card = video.closest('[data-kiwibit-video-card]');
    if (card) card.classList.remove('is-playing');
    video.controls = false;
    try {
      video.currentTime = 0;
    } catch (error) {
      // Ignore reset issues for protected media states.
    }
  }

  pauseAllVideos(activeVideo) {
    this.videos.forEach((video) => {
      if (video === activeVideo) return;
      video.pause();
      video.controls = false;
      const card = video.closest('[data-kiwibit-video-card]');
      if (card) card.classList.remove('is-playing');
    });
  }

  pauseInactiveGroupVideos() {
    if (!this.desktopGroups.length) return;

    this.desktopGroups.forEach((group, index) => {
      if (index === this.desktopIndex) return;

      group.querySelectorAll('video.kiwibit-video-showcase__video').forEach((video) => {
        video.pause();
        video.controls = false;
        const card = video.closest('[data-kiwibit-video-card]');
        if (card) card.classList.remove('is-playing');
      });
    });
  }

  pauseInactiveMobileGroupVideos() {
    if (!this.mobileGroups.length) return;

    this.mobileGroups.forEach((group, index) => {
      if (index === this.mobileIndex) return;

      group.querySelectorAll('video.kiwibit-video-showcase__video').forEach((video) => {
        video.pause();
        video.controls = false;
        const card = video.closest('[data-kiwibit-video-card]');
        if (card) card.classList.remove('is-playing');
      });
    });
  }
}

if (!customElements.get('kiwibit-video-showcase-carousel')) {
  customElements.define('kiwibit-video-showcase-carousel', KiwibitVideoShowcaseCarousel);
}
